const SCORE_FIELDS = ["score", "scorei", "scoreii", "scoreiii"];

export const SDI_TABLE_KEYS = [
  "gaSupport", "generalAssemblyDecision", "generalAssemblyManagementActions",
  "waterServices", "electricityServices", "sanitationServices", "maintenanceInfrastructure",
  "clientServiceCharter", "transportationNetworkService",
  "buildingInspectorateUnit", "permitProcessingIssuance", "streetNaming",
  "socialProtectionServices", "districtHotlineNumber", "pwdService",
  "deepeningGenderMainstreaming", "nutritionIntervention",
  "sanitationServiceProviders", "dumpingSite", "foodVendors", "publicSchoolFacility",
  "climateChangeIntervention", "districtLEDActivityPlan", "businessAndJobPromotion",
  "agroProcessingFacilitySupport", "businessCommunityEngagement",
];

/** SDI 1.1 and SDI 1.2 stay on live scores and are not adjusted by target calibration. */
export const LIVE_LOCKED_SDI_TABLE_KEYS = [
  "generalAssemblyDecision",
  "generalAssemblyManagementActions",
];

export const PI_TABLE_KEYS = [
  "aapImplementation", "monitoringProjectAndActivity", "contractManagementAndAdmins",
  "followUpDeduction", "environmentalAndSocialSafeGuard", "rateableRevenue",
  "auditCommitteeResponsiveness", "auditInfractions",
  "educationServiceSupport", "healthServiceSupport", "agricultureSupport",
];

export const CI_KEY_BY_SLUG = {
  "ci1.1": "gaMeeting",
  "ci1.3": "subStructureMeeting",
  "ci2.1": "executiveCommitteeMember",
  "ci3.1": "spcEntityTenderCommittee",
  "ci3.2": "internalAuditUnitFunctionality",
  "ci3.3": "auditCommitteeMeeting",
  "ci4.2": "aapPublication",
  "ci4.3": "auditorGeneralGAMeeting",
  "ci4.4": "townHallMeeting",
};

export function tableScoreTotal(entry) {
  if (!entry) return 0;
  return SCORE_FIELDS.reduce((sum, field) => {
    const value = entry[field];
    return sum + (typeof value === "number" ? value : 0);
  }, 0);
}

function readFulfillmentValue(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value?.fulfillment === "string") return value.fulfillment;
  return null;
}

/** Read CI fulfillment status from a live indicator snapshot entry. */
export function extractLiveCiFulfillmentStatus(tableKey, entry) {
  if (!entry) return "Not Fulfilled";

  switch (tableKey) {
    case "gaMeeting":
      return readFulfillmentValue(entry.data) || "Not Fulfilled";
    case "subStructureMeeting":
      return (
        entry.fulfillment ||
        readFulfillmentValue(entry.data) ||
        "Not Fulfilled"
      );
    case "spcEntityTenderCommittee":
      return readFulfillmentValue(entry.data) || "Not Fulfilled";
    case "executiveCommitteeMember":
      return entry.fulfillment || readFulfillmentValue(entry.data) || "Not Fulfilled";
    case "internalAuditUnitFunctionality":
      return entry.fulfillment || "Not Fulfilled";
    case "auditCommitteeMeeting":
      return (
        entry.fulfillment ||
        readFulfillmentValue(entry.meetings) ||
        "Not Fulfilled"
      );
    case "aapPublication":
      return readFulfillmentValue(entry.publication) || "Not Fulfilled";
    case "auditorGeneralGAMeeting":
      return entry.fulfillment || "Not Fulfilled";
    case "townHallMeeting":
      return readFulfillmentValue(entry.meetings) || "Not Fulfilled";
    default:
      return entry.fulfillment || "Not Fulfilled";
  }
}

/** Extract SDI/PI score fields from live DDDP indicator getData() output. */
export function extractLiveScoreTables(liveTables) {
  if (!liveTables) return {};
  const keys = [...SDI_TABLE_KEYS, ...PI_TABLE_KEYS];
  const scores = {};

  for (const key of keys) {
    const entry = liveTables[key];
    if (!entry) continue;
    scores[key] = {
      ...(typeof entry.score === "number" ? { score: entry.score } : {}),
      ...(typeof entry.scorei === "number" ? { scorei: entry.scorei } : {}),
      ...(typeof entry.scoreii === "number" ? { scoreii: entry.scoreii } : {}),
      ...(typeof entry.scoreiii === "number" ? { scoreiii: entry.scoreiii } : {}),
      ...(typeof entry.maxScore === "number" ? { maxScore: entry.maxScore } : {}),
    };
  }

  return scores;
}

/** Extract CI fulfillment state from live tables for configured slugs. */
export function extractLiveCiFulfillment(liveTables, ciSlugs = []) {
  if (!liveTables || !ciSlugs.length) return {};
  const fulfillment = {};

  for (const slug of ciSlugs) {
    const tableKey = CI_KEY_BY_SLUG[String(slug).toLowerCase()];
    if (!tableKey || !liveTables[tableKey]) continue;
    fulfillment[tableKey] = {
      fulfillment: extractLiveCiFulfillmentStatus(tableKey, liveTables[tableKey]),
      entry: liveTables[tableKey],
    };
  }

  return fulfillment;
}

export function summarizeLiveTableScores(liveTables) {
  return {
    sdiTotal: SDI_TABLE_KEYS.reduce((sum, key) => sum + tableScoreTotal(liveTables?.[key]), 0),
    piTotal: PI_TABLE_KEYS.reduce((sum, key) => sum + tableScoreTotal(liveTables?.[key]), 0),
  };
}

/** Attach dynamic score + CI snapshot onto a target requirements object. */
export function buildDynamicDistrictTarget(baseTarget, liveTables) {
  if (!baseTarget || !liveTables) return baseTarget;

  return {
    ...baseTarget,
    tables: extractLiveScoreTables(liveTables),
    ciFulfillment: extractLiveCiFulfillment(liveTables, baseTarget.ciSlugs || []),
    liveTotals: summarizeLiveTableScores(liveTables),
    source: baseTarget.source ? `${baseTarget.source}+live` : "live",
  };
}
